/**
 * Signed-in account chip for the header: initials, and a popover with the
 * user's name/email and sign-out. Renders nothing when auth is off, so the
 * header is unchanged in unconfigured/dev environments.
 */

import { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { msalConfigured, authDevBypass } from './msal';
import { ownerWindowOf } from '../components/common/ownerWindow';
import './AuthGate.css';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function AccountMenuInner() {
  const { instance } = useMsal();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

  useEffect(() => {
    if (!open) return;
    const close = (ev: MouseEvent) => {
      if (!ref.current?.contains(ev.target as Node)) setOpen(false);
    };
    const key = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    const win = ownerWindowOf(ref.current);
    win.addEventListener('mousedown', close);
    win.addEventListener('keydown', key);
    return () => {
      win.removeEventListener('mousedown', close);
      win.removeEventListener('keydown', key);
    };
  }, [open]);

  if (!account) return null;
  const name = account.name ?? account.username;

  return (
    <div className="account-menu" ref={ref}>
      <button
        type="button"
        className="account-menu-chip"
        aria-label={`Account: ${name}`}
        aria-expanded={open}
        title={name}
        onClick={() => setOpen((v) => !v)}
      >
        {initialsOf(name)}
      </button>
      {open && (
        <div className="account-menu-pop" role="menu">
          <div>
            <div className="account-menu-name">{name}</div>
            <div className="account-menu-email">{account.username}</div>
          </div>
          <button
            type="button"
            className="account-menu-signout"
            role="menuitem"
            onClick={() => void instance.logoutRedirect()}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function AccountMenu() {
  if (!msalConfigured || authDevBypass) return null;
  return <AccountMenuInner />;
}
